import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Company, DBCompany } from "@/types";

// Re-export types for backward compatibility
export type { Company } from "@/types";

const isValidUrl = (url: string | null): boolean => {
  if (!url || url === 'Unknown' || url === 'unknown' || url === '0' || url === '0.0') return false;
  return url.startsWith('http://') || url.startsWith('https://');
};

// Generate placeholder logo URL using UI Avatars
const getPlaceholderLogo = (name: string): string => {
  const initials = name
    .split(' ')
    .map(word => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=6366f1&color=fff&size=128&bold=true`;
};

const transformCompany = (company: DBCompany): Company => ({
  id: company.id,
  name: company.name,
  slug: company.slug,
  description: company.description,
  country: company.country,
  region: company.region,
  foundedYear: company.founded_year || undefined,
  website: isValidUrl(company.website) ? company.website! : undefined,
  logoUrl: isValidUrl(company.logo_url) ? company.logo_url! : getPlaceholderLogo(company.name),
  headquarters: company.headquarters || undefined,
  employees: company.employees || undefined,
  funding: company.funding || undefined,
  valuation: company.valuation || undefined,
  ceo: company.ceo || undefined,
  focusAreas: company.focus_areas,
  isApproved: company.is_approved,
});

export const useCompanies = () => {
  return useQuery({
    queryKey: ['companies'],
    queryFn: async (): Promise<Company[]> => {
      // Get companies
      const { data: companies, error: companiesError } = await supabase
        .from('companies')
        .select('*')
        .order('name');

      if (companiesError) throw companiesError;
      if (!companies || companies.length === 0) return [];

      // Get robot counts per company
      const { data: robots, error: robotsError } = await supabase
        .from('humanoid_robots')
        .select('id, name, company');

      if (robotsError) throw robotsError;

      // Group robots by company name
      const robotsByCompany = new Map<string, { id: string; name: string }[]>();
      robots?.forEach(robot => {
        const existing = robotsByCompany.get(robot.company) || [];
        existing.push({ id: robot.id, name: robot.name });
        robotsByCompany.set(robot.company, existing);
      });

      return companies.map(c => {
        const company = transformCompany(c);
        const companyRobots = robotsByCompany.get(company.name) || [];
        return {
          ...company,
          robotCount: companyRobots.length,
          robots: companyRobots,
        };
      });
    },
  });
};

export const useCompany = (slug: string | undefined) => {
  return useQuery({
    queryKey: ['company', slug],
    queryFn: async (): Promise<Company | null> => {
      if (!slug) return null;

      const { data: company, error } = await supabase
        .from('companies')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();

      if (error) throw error;
      if (!company) return null;

      // Get robots for this company
      const { data: robots } = await supabase
        .from('humanoid_robots')
        .select('id, name')
        .eq('company', company.name);

      const transformed = transformCompany(company);
      return {
        ...transformed,
        robotCount: robots?.length || 0,
        robots: robots || [],
      };
    },
    enabled: !!slug,
  });
};
